import React from 'react';
import { cn } from '../lib/cn';

/**
 * KpiCard — tarjeta especializada para mostrar un KPI numérico.
 *
 * Props:
 *   - label:      Texto en mayúsculas pequeño (ej: "Total llamadas")
 *   - value:      Valor principal grande (ej: "70.895" o "73,8%")
 *   - icon:       Componente Lucide u otro nodo
 *   - iconColor:  Color del icono ('primary' | 'success' | 'warning' | 'danger' | 'muted')
 *   - subtitle:   Texto pequeño debajo del valor (ej: "73,8% del total")
 *   - trend:      Opcional { value: '12,4%', direction: 'up' | 'down' }
 *   - progress:   Opcional 0-100 para mostrar barra de progreso
 *
 * Uso:
 *   <KpiCard
 *     label="Total llamadas"
 *     value="70.895"
 *     icon={Phone}
 *     subtitle="73,8% del total"
 *     trend={{ value: '12,4%', direction: 'up' }}
 *   />
 */
export const KpiCard = ({
  label,
  value,
  icon: Icon,
  iconColor = 'primary',
  subtitle,
  trend,
  progress,
  className,
  ...props
}) => {
  const iconColorClass = {
    primary: 'text-[hsl(var(--brand-light))]',
    success: 'text-emerald-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',
    muted: 'text-slate-400',
  }[iconColor] || '';

  return (
    <div
      className={cn(
        "bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3.5",
        "hover:border-slate-300 transition-colors",
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-semibold">
          {label}
        </span>
        {Icon && (
          typeof Icon === 'function'
            ? <Icon className={cn("size-3.5", iconColorClass)} strokeWidth={1.75} />
            : <span className={iconColorClass}>{Icon}</span>
        )}
      </div>

      <div className="text-2xl font-semibold text-[hsl(var(--foreground))] tabular-nums leading-none">
        {value}
      </div>

      {progress !== undefined && (
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-[hsl(var(--brand-light))] rounded-full transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}

      {(subtitle || trend) && (
        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">
          {trend && (
            <span className={cn(
              "font-medium mr-1",
              trend.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
            )}>
              {trend.direction === 'up' ? '↑' : '↓'} {trend.value}
            </span>
          )}
          {subtitle}
        </div>
      )}
    </div>
  );
};
